import { Inject, Injectable } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { SITE_ANNOUNCEMENTS_LOADER } from '@galabid/gb-dashboard/staff/data-access-site-announcements'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { TranslateService } from '@ngx-translate/core'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { GbAnnouncementsListDialogComponent } from 'libs/gb-dashboard/announcements/feature/src/lib/gb-announcements-list-dialog/gb-announcements-list-dialog.component'
import { GbCreateSiteAnnouncementDialogComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-create-site-announcement-dialog/gb-create-site-announcement-dialog.component'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { catchError, exhaustMap, finalize, map, mergeMap, of, switchMap, tap } from 'rxjs'
import { SiteAnnouncementsApiService } from './site-announcements-api.service'
import * as SiteAnnouncementsActions from './site-announcements.actions'

@Injectable()
export class SiteAnnouncementsEffects {
  loadSiteAnnouncements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteAnnouncementsActions.loadSiteAnnouncements),
      switchMap((action) => {
        return this.siteAnnouncementsApiService
          .getSiteAnnouncements(action.limit || this.appConfig.itemsPageSize, action.offset || 0)
          .pipe(
            mergeMap((res) => {
              return [
                SiteAnnouncementsActions.loadSiteAnnouncementsSuccess({
                  site_announcements: res.results,
                }),
                SiteAnnouncementsActions.setUnreadAnnouncements(),
              ]
            }),
            catchError((error) => {
              return of(
                SiteAnnouncementsActions.loadSiteAnnouncementsFailure({
                  error,
                }),
              )
            }),
          )
      }),
    ),
  )

  openCreateSiteAnnouncementDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SiteAnnouncementsActions.selectSiteAnnouncement),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbCreateSiteAnnouncementDialogComponent, {
            disableClose: true,
            maxWidth: '800px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  closeCreateSiteAnnouncementDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SiteAnnouncementsActions.createSiteAnnouncementSuccess,
          SiteAnnouncementsActions.updateSiteAnnouncementSuccess,
        ),
        map(() => this.gbDashboardDialogService.closeDialog(GbCreateSiteAnnouncementDialogComponent)),
      ),
    { dispatch: false },
  )

  openAnnouncementListDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SiteAnnouncementsActions.openAnnouncementListDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbAnnouncementsListDialogComponent, {
            width: '850px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  createSiteAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteAnnouncementsActions.createSiteAnnouncement),

      switchMap((action) => {
        return this.siteAnnouncementsApiService.createSiteAnnouncement(action.site_announcement).pipe(
          map((site_announcement) => {
            return SiteAnnouncementsActions.createSiteAnnouncementSuccess({
              site_announcement,
            })
          }),
          catchError((error) => {
            return of(
              SiteAnnouncementsActions.createSiteAnnouncementFailure({
                error,
              }),
            )
          }),
        )
      }),
    ),
  )

  updateSiteAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteAnnouncementsActions.updateSiteAnnouncement),
      switchMap((action) => {
        return this.siteAnnouncementsApiService.updateSiteAnnouncement(action.site_announcement).pipe(
          map((site_announcement) => {
            return SiteAnnouncementsActions.updateSiteAnnouncementSuccess({
              update: {
                id: site_announcement.uuid,
                changes: site_announcement,
              },
            })
          }),
          catchError((error) => {
            return of(
              SiteAnnouncementsActions.updateSiteAnnouncementFailure({
                error,
              }),
            )
          }),
        )
      }),
    ),
  )

  deleteSiteAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteAnnouncementsActions.deleteSiteAnnouncement),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'SITE_ANNOUNCEMENTS.DELETE_CONFIRM_TITLE',
            body: this.translateService.instant('SITE_ANNOUNCEMENTS.DELETE_CONFIRM_DESC', { title: action.title }),
            confirmButtonText: 'SITE_ANNOUNCEMENTS.DELETE_CONFIRM_OK',
            cancelButtonText: 'SITE_ANNOUNCEMENTS.DELETE_CONFIRM_CANCEL',
          },
          action,
        ),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(SITE_ANNOUNCEMENTS_LOADER.deleteSiteAnnouncement.id)
      }),
      switchMap((action) => {
        return this.siteAnnouncementsApiService.deleteSiteAnnouncement(action.uuid).pipe(
          map((res) => {
            return SiteAnnouncementsActions.deleteSiteAnnouncementSuccess({
              uuid: action.uuid,
            })
          }),
          catchError((error) => {
            return of(
              SiteAnnouncementsActions.deleteSiteAnnouncementFailure({
                error,
              }),
            )
          }),
          finalize(() => {
            this.gbLoaderService.hideLoaderDialog(SITE_ANNOUNCEMENTS_LOADER.deleteSiteAnnouncement.id)
          }),
        )
      }),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SiteAnnouncementsActions.loadSiteAnnouncementsFailure,
          SiteAnnouncementsActions.createSiteAnnouncementFailure,
          SiteAnnouncementsActions.updateSiteAnnouncementFailure,
          SiteAnnouncementsActions.deleteSiteAnnouncementFailure,
        ),
        map((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private siteAnnouncementsApiService: SiteAnnouncementsApiService,
    private readonly actions$: Actions,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private translateService: TranslateService,
    private gbLoaderService: GbLoaderService,
    @Inject(APP_CONFIG) protected appConfig: EnvironmentModel,
  ) {}
}
